import { useState } from 'react';
import type { ApiClient, MemberRow, Permission } from '@gc/api-client';
import { useResource } from '@gc/ui-state';
import { DocumentUpload } from './Documents.js';
import { GadgetForm } from './GadgetForm.js';
import { LifecycleActions } from './LifecycleActions.js';
import { Repairs } from './Repairs.js';
import { Timeline } from './Timeline.js';
import { rupees, stateLabel, words } from './labels.js';

export type GadgetDetailApi = Pick<
  ApiClient,
  'gadgets' | 'lifecycle' | 'repairs' | 'members' | 'uploadDocument' | 'getTimeline' | 'getDocumentUrl'
>;

const day = (v: string | null): string =>
  v ? new Date(v).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export function GadgetDetail({ gadgetId, api, permissions, onClose, onChanged }: {
  gadgetId: string;
  api: GadgetDetailApi;
  permissions: readonly Permission[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const [version, setVersion] = useState(0);
  const [editing, setEditing] = useState(false);
  const can = (p: Permission) => permissions.includes(p);

  const { load } = useResource(() => api.gadgets.get(gadgetId), [gadgetId, version]);
  const { load: cost } = useResource(() => api.lifecycle.cost(gadgetId), [gadgetId, version]);
  const { load: people } = useResource(
    () => (can('users:manage') ? api.members.list() : Promise.resolve({ items: [] as MemberRow[] })),
    [gadgetId],
  );

  const changed = () => {
    setVersion((n) => n + 1);
    onChanged();
  };

  if (load.kind === 'loading') return <p>Loading gadget…</p>;
  if (load.kind === 'error') return <p role="alert" data-testid="gadget-error">Could not load this gadget: {load.message}</p>;

  const g = load.data;
  const members = people.kind === 'ready' ? people.data.items : [];
  const ownerEmail = members.find((m) => m.userId === g.ownerUserId)?.email;

  return (
    <div data-testid="gadget-detail">
      <div className="panel-head">
        <h3><code>{g.shortId}</code> {g.assetTag} · {g.make} {g.model}</h3>
        <span className={`pill ${g.status}`} data-testid="gadget-status">{stateLabel(g.status)}</span>
        <div className="form-actions">
          {can('gadgets:edit') && !editing && g.status !== 'disposed' && (
            <button type="button" onClick={() => setEditing(true)} data-testid="action-edit">Edit</button>
          )}
          <button type="button" onClick={onClose} data-testid="close-gadget">Close</button>
        </div>
      </div>

      {editing
        ? (
          <GadgetForm
            api={api}
            mode={{ kind: 'edit', gadget: g }}
            onCancel={() => setEditing(false)}
            onDone={() => { setEditing(false); changed(); }}
          />
        )
        : (
          <dl className="facts">
            <div><dt>Category</dt><dd>{words(g.category)}</dd></div>
            <div><dt>Serial</dt><dd>{g.serial ?? '—'}</dd></div>
            <div><dt>IMEI</dt><dd>{g.imei ?? '—'}</dd></div>
            <div><dt>Owner</dt><dd data-testid="gadget-owner">{g.ownerTeam ?? ownerEmail ?? (g.ownerUserId ? 'A person' : '—')}</dd></div>
            <div><dt>Location</dt><dd data-testid="gadget-location">{g.location ?? '—'}</dd></div>
            <div><dt>Supplier</dt><dd>{g.supplier ?? '—'}</dd></div>
            <div><dt>Purchased</dt><dd>{day(g.purchasedOn)}</dd></div>
            <div><dt>Warranty ends</dt><dd>{day(g.warrantyEnds)}</dd></div>
            <div><dt>Guarantee ends</dt><dd>{day(g.guaranteeEnds)}</dd></div>
            <div><dt>Price</dt><dd>{rupees(g.priceMinor)}</dd></div>
            <div><dt>Repairs to date</dt><dd data-testid="cost-repairs">{cost.kind === 'ready' ? rupees(cost.data.repairMinor) : '…'}</dd></div>
            <div><dt>Cost of ownership</dt><dd data-testid="cost-total">{cost.kind === 'ready' ? rupees(cost.data.totalMinor) : '…'}</dd></div>
          </dl>
        )}
      {g.notes && !editing && <p>{g.notes}</p>}

      {can('gadgets:edit') && (
        <LifecycleActions
          gadget={g}
          api={api}
          people={members}
          onChanged={changed}
          onDeleted={() => { onChanged(); onClose(); }}
        />
      )}

      <Repairs gadget={g} api={api} canLog={can('repairs:log')} onChanged={changed} />

      {can('gadgets:edit') && g.status !== 'disposed' && (
        <DocumentUpload gadgetId={g.id} api={api} onUploaded={changed} />
      )}

      <Timeline key={version} gadgetId={g.id} api={api} />
    </div>
  );
}
